import axios from 'axios';
import { getCurrentUser } from './auth';
import type { User } from '../types';

const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production'
    ? '/api'
    : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`,
});

api.interceptors.request.use(
  (config) => {
    const user = getCurrentUser();
    if (user && user.access_token) {
      config.headers['Authorization'] = `Bearer ${user.access_token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Global auth error handling: if token is invalid/expired or backend restarted, force logout
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      try {
        localStorage.removeItem('user');
      } catch {}
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        if (currentPath !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export interface Job {
  id: number;
  job_type: 'file' | 'transcript';
  transcript?: string;
  filename?: string;
  original_filename?: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'converting';
  model?: string;
  meeting_title?: string;
  meeting_date?: string;
  attendees?: string[];
  context?: string;
  template_id?: number;
  generated_notes?: string;
  error_message?: string;
  created_at: string;
  updated_at?: string;
}

export interface Template {
  id: number;
  name: string;
  description?: string;
  instructions: string;
  is_default: boolean;
  created_at: string;
  updated_at?: string;
}

export interface JobSubmission {
  meeting_title?: string;
  meeting_date?: string;
  attendees?: string;
  context?: string;
  template_id?: number;
  model?: string;
}

export interface TemplateRequest {
  name: string;
  description?: string;
  instructions: string;
  is_default?: boolean;
}

export interface ModelOption {
  value: string;
  label: string;
  description?: string;
}

// Job API
export const jobsApi = {
  uploadFile: async (file: File, jobData: JobSubmission): Promise<Job> => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Always append form fields, even if empty (backend expects them)
    formData.append('meeting_title', jobData.meeting_title || '');
    formData.append('meeting_date', jobData.meeting_date || '');
    formData.append('attendees', jobData.attendees || '');
    formData.append('context', jobData.context || '');
    
    if (jobData.template_id !== undefined && jobData.template_id !== null) {
      formData.append('template_id', jobData.template_id.toString());
    }
    if (jobData.model) {
      formData.append('model', jobData.model);
    }

    const response = await api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadTranscript: async (transcript: string, jobData: JobSubmission): Promise<Job> => {
    const payload = {
      ...jobData,
      transcript,
      attendees: jobData.attendees
        ? jobData.attendees.split(',').map(name => name.trim()).filter(name => name)
        : [],
    };
    const response = await api.post('/transcripts/upload', payload);
    return response.data;
  },

  getJobs: async (): Promise<Job[]> => {
    const response = await api.get('/jobs/');
    return response.data;
  },

  getJob: async (jobId: number): Promise<Job> => {
    const response = await api.get(`/jobs/${jobId}`);
    return response.data;
  },

  getJobStatus: async (jobId: number): Promise<{ id: number; status: string; error_message?: string }> => {
    const response = await api.get(`/jobs/status/${jobId}`);
    return response.data;
  },

  deleteJobs: async (jobIds: number[]): Promise<void> => {
    await api.delete('/jobs/', { data: { job_ids: jobIds } });
  },
};

// Template API
export const templatesApi = {
  getTemplates: async (): Promise<Template[]> => {
    const response = await api.get('/templates/');
    return response.data;
  },

  getTemplate: async (templateId: number): Promise<Template> => {
    const response = await api.get(`/templates/${templateId}`);
    return response.data;
  },

  createTemplate: async (template: TemplateRequest): Promise<Template> => {
    const response = await api.post('/templates/', template);
    return response.data;
  },

  updateTemplate: async (templateId: number, template: TemplateRequest): Promise<Template> => {
    const response = await api.put(`/templates/${templateId}`, template);
    return response.data;
  },

  deleteTemplate: async (templateId: number): Promise<void> => {
    await api.delete(`/templates/${templateId}`);
  },

  initializeDefaults: async (): Promise<void> => {
    await api.post('/templates/init-defaults');
  },
};

// Health check
export const healthApi = {
  check: async (): Promise<{ status: string }> => {
    const response = await api.get('/files/health');
    return response.data;
  },
};

// Settings API
export const settingsApi = {
  getSettings: async (): Promise<any> => {
    const response = await api.get('/settings/');
    return response.data;
  },
  getModels: async (): Promise<ModelOption[]> => {
    const response = await api.get('/settings/models');
    return response.data;
  },
  updateSettings: async (settings: any): Promise<any> => {
    const response = await api.put('/settings/', settings);
    return response.data;
  },
};

// Admin API
export const adminApi = {
  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  approveUser: async (userId: number): Promise<User> => {
    const response = await api.post(`/admin/users/${userId}/approve`);
    return response.data;
  },
  rejectUser: async (userId: number): Promise<void> => {
    await api.post(`/admin/users/${userId}/reject`);
  },
  disableUser: async (userId: number): Promise<User> => {
    const response = await api.post(`/admin/users/${userId}/disable`);
    return response.data;
  },
  enableUser: async (userId: number): Promise<User> => {
    const response = await api.post(`/admin/users/${userId}/enable`);
    return response.data;
  },
  createUser: async (payload: { email: string; is_admin?: boolean; is_active?: boolean }): Promise<{ user: User; reset_token: string }> => {
    const response = await api.post('/admin/users', payload);
    return response.data;
  },
  generateResetToken: async (userId: number): Promise<{ reset_token: string }> => {
    const response = await api.post(`/admin/users/${userId}/reset-password-token`);
    return response.data;
  },
}

export default api;
